/**
 * Phase 3 — Batch Offload Dialog
 *
 * Fetches offload candidates from the server and lets the user select
 * which messages to download to their device in bulk.
 * Each download: SAS URL fetch → IndexedDB store → confirm (delete server copy).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, Alert,
  List, ListItem, ListItemText, Checkbox, Chip,
  LinearProgress, FormControl, InputLabel, Select, MenuItem,
  Divider,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import api from '../../services/api';
import {
  downloadAndStoreViaApi,
} from '../../services/offloadedMessageStore';

interface Candidate {
  mailId:    string;
  subject:   string;
  sentDate:  string;
  fromName:  string;
  blobTier:  string;
  sizeBytes: number;
  ageDays:   number;
}

interface CandidatesResponse {
  count:          number;
  totalSizeBytes: number;
  totalSizeMb:    number;
  messages:       Candidate[];
}

function fmtSize(b: number) {
  if (b < 1024)      return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1_048_576).toFixed(2)} MB`;
}

function fmtDate(d: string) {
  if (!d || d.length < 8) return d;
  return `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(6, 8)}`;
}

interface Props {
  open:    boolean;
  onClose: () => void;
}

type ViewState = 'loading' | 'select' | 'processing' | 'done' | 'error';

const BatchOffloadDialog: React.FC<Props> = ({ open, onClose }) => {
  const [viewState, setViewState]         = useState<ViewState>('loading');
  const [candidates, setCandidates]       = useState<Candidate[]>([]);
  const [selected, setSelected]           = useState<Set<string>>(new Set());
  const [olderThanDays, setOlderThanDays] = useState<number | ''>('');
  const [progress, setProgress]           = useState({ done: 0, total: 0 });
  const [errorMsg, setErrorMsg]           = useState('');

  const fetchCandidates = async () => {
    setViewState('loading');
    try {
      const params = olderThanDays ? `?olderThanDays=${olderThanDays}` : '';
      const resp   = await api.get<CandidatesResponse>(`/api/offload/candidates${params}`);
      setCandidates(resp.messages);
      setSelected(new Set(resp.messages.map((c: Candidate) => c.mailId)));
      setViewState('select');
    } catch {
      setErrorMsg('Could not load candidates. Make sure blob storage is active.');
      setViewState('error');
    }
  };

  useEffect(() => {
    if (open) {
      setProgress({ done: 0, total: 0 });
      fetchCandidates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleAll = () => {
    if (selected.size === candidates.length)
      setSelected(new Set());
    else
      setSelected(new Set(candidates.map(c => c.mailId)));
  };

  const toggle = (mailId: string) => {
    const next = new Set(selected);
    next.has(mailId) ? next.delete(mailId) : next.add(mailId);
    setSelected(next);
  };

  const handleDownload = async () => {
    const toProcess = candidates.filter(c => selected.has(c.mailId));
    if (!toProcess.length) return;

    setViewState('processing');
    setProgress({ done: 0, total: toProcess.length });

    for (const c of toProcess) {
      try {
        const infoResp = await api.get<{ sizeBytes: number }>(
          `/api/offload/${c.mailId}/download-url`
        );
        const sizeBytes = infoResp.sizeBytes ?? c.sizeBytes;
        // Use API proxy instead of direct SAS URL to avoid browser CORS restrictions
        const ok = await downloadAndStoreViaApi(
          c.mailId,
          { mailId: c.mailId, subject: c.subject, fromName: c.fromName,
            sentDate: c.sentDate, sizeBytes },
          (path) => api.get<string>(path, { responseType: 'text' })
        );
        if (ok) {
          await api.post(`/api/offload/${c.mailId}/confirm`);
        }
      } catch { /* continue with next */ }
      setProgress(p => ({ ...p, done: p.done + 1 }));
    }

    setViewState('done');
  };

  const selectedSize = candidates
    .filter(c => selected.has(c.mailId))
    .reduce((s, c) => s + c.sizeBytes, 0);

  const pct = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Download Messages to This Device</DialogTitle>

      <DialogContent>
        {/* ── Loading ──────────────────────────────────────────────── */}
        {viewState === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Loading offload candidates…</Typography>
          </Box>
        )}

        {/* ── Select ───────────────────────────────────────────────── */}
        {viewState === 'select' && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Server copies will be permanently deleted</strong> after download.
              Messages will only exist on this browser.
            </Alert>

            {/* Age filter */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by age</InputLabel>
                <Select
                  label="Filter by age"
                  value={olderThanDays}
                  onChange={e => setOlderThanDays(e.target.value as number | '')}
                >
                  <MenuItem value="">All messages</MenuItem>
                  <MenuItem value={30}>Older than 30 days</MenuItem>
                  <MenuItem value={90}>Older than 90 days (Cool)</MenuItem>
                  <MenuItem value={180}>Older than 180 days</MenuItem>
                  <MenuItem value={365}>Older than 1 year (Archive)</MenuItem>
                </Select>
              </FormControl>
              <Button size="small" onClick={fetchCandidates}>Apply</Button>
            </Box>

            {candidates.length === 0 ? (
              <Typography color="text.secondary">
                No eligible messages found. Messages must be in blob storage (sent after Phase 2 enabled).
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox
                    checked={selected.size === candidates.length}
                    indeterminate={selected.size > 0 && selected.size < candidates.length}
                    onChange={toggleAll}
                    size="small"
                  />
                  <Typography variant="body2">
                    {selected.size} of {candidates.length} selected · {fmtSize(selectedSize)}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 1 }} />

                <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {candidates.map(c => (
                      <ListItem key={c.mailId} disablePadding
                        sx={{ '&:hover': { bgcolor: '#f5f5f5' }, cursor: 'pointer' }}
                        onClick={() => toggle(c.mailId)}
                      >
                        <Checkbox
                          checked={selected.has(c.mailId)}
                          size="small"
                          sx={{ py: 0.5 }}
                        />
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {c.subject}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {c.fromName} · {fmtDate(c.sentDate)} · {fmtSize(c.sizeBytes)} · {c.ageDays}d old
                            </Typography>
                          }
                        />
                        <Chip
                          label={c.blobTier}
                          size="small"
                          sx={{
                            fontSize: 10, height: 18, mr: 1,
                            bgcolor: c.blobTier === 'COOL' ? '#e3f2fd' : c.blobTier === 'ARCHIVE' ? '#fce4ec' : '#f5f5f5',
                            color:   c.blobTier === 'COOL' ? '#1565c0' : c.blobTier === 'ARCHIVE' ? '#880e4f' : '#424242',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}
          </>
        )}

        {/* ── Processing ───────────────────────────────────────────── */}
        {viewState === 'processing' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography gutterBottom>
              Downloading {progress.done} / {progress.total}…
            </Typography>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Server copies are deleted after each successful download.
            </Typography>
          </Box>
        )}

        {/* ── Done ─────────────────────────────────────────────────── */}
        {viewState === 'done' && (
          <Alert severity="success">
            ✅ Downloaded {progress.done} message{progress.done !== 1 ? 's' : ''} to your device.
            Server copies have been deleted. Find them with a <strong>Local</strong> badge in your inbox.
          </Alert>
        )}

        {/* ── Error ────────────────────────────────────────────────── */}
        {viewState === 'error' && (
          <Alert severity="error">{errorMsg}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        {viewState === 'select' && candidates.length > 0 && selected.size > 0 && (
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={handleDownload}
          >
            Download {selected.size} message{selected.size !== 1 ? 's' : ''} ({fmtSize(selectedSize)})
          </Button>
        )}
        {(viewState === 'select' && (candidates.length === 0 || selected.size === 0)) && (
          <Button variant="contained" onClick={onClose}>Close</Button>
        )}
        {(viewState === 'done' || viewState === 'error') && (
          <Button variant="contained" onClick={onClose}>Close</Button>
        )}
        {viewState === 'select' && (
          <Button onClick={onClose} color="inherit">Cancel</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchOffloadDialog;
